import { useMutation, useQueryClient } from "react-query";
import { MigrationSet, SourceSystemType, useSmokeballApp } from "../shared";
import log from "../shared/utils/log";
import { requestHelper, ServerError, UnauthorizedError } from "../shared/utils";
import { useState } from "react";

const fetchNewMigration = async ({
  accountId,
  token,
  importTypes,
  dataTypeSubOptions,
  sourceSystem,
  dataOrigin,
  exportReason,
}: {
  accountId: string;
  token?: () => Promise<string>;
  importTypes: string[];
  dataTypeSubOptions?: string[];
  sourceSystem?: SourceSystemType;
  dataOrigin?: string;
  exportReason?: string;
}): Promise<MigrationSet> => {

  // Join the import types into a comma-separated string
  const importTypesQuery = importTypes.join(",");

  // Construct the endpoint with query parameters
  let endpoint = `/init?importTypes=${encodeURIComponent(importTypesQuery)}`;
  if (dataTypeSubOptions) {
    const dataTypeSubOptionsQuery = dataTypeSubOptions.join(",");
    endpoint += `&dataTypeSubOptions=${encodeURIComponent(dataTypeSubOptionsQuery)}`;
  }
  if (sourceSystem) {
    endpoint += `&sourceSystem=${encodeURIComponent(sourceSystem)}`;
  }
  if (dataOrigin) {
    endpoint += `&dataOrigin=${encodeURIComponent(dataOrigin)}`;
  }
  if (exportReason) {
    endpoint += `&exportReason=${encodeURIComponent(exportReason)}`;
  }
  log("endpoint: ", endpoint);

  const data = await requestHelper(
    accountId,
    endpoint,
    "Failed to initialise new import/export.",
    "POST",
    token,
  );
  log("Initialised import/export: ", data);

  return data;
};

export const useNewMigration = () => {
  const { auth, context } = useSmokeballApp();
  const { mutateAsync, isLoading } = useMutation(fetchNewMigration);
  const accountId = context?.firmId || "";
  const token = auth?.token;
  const queryClient = useQueryClient();

  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [isServerError, setIsServerError] = useState(false);

  const handleNewMigrationClick = async ({
    selectedImportTypes,
    dataTypeSubOptions,
    sourceSystem,
    dataOrigin,
    exportReason,
  }: {
    selectedImportTypes: string[];
    dataTypeSubOptions?: string[];
    sourceSystem?: SourceSystemType;
    dataOrigin?: string;
    exportReason?: string;
  }) => {
    try {
      const result = await mutateAsync({
        accountId,
        token,
        importTypes: selectedImportTypes,
        dataTypeSubOptions,
        sourceSystem,
        dataOrigin,
        exportReason,
      });
      setIsUnauthorized(false); // Reset on success
      setIsServerError(false); // Reset on success
      queryClient.invalidateQueries(["migrations", accountId]);
      log("Selected import types: ", selectedImportTypes);
      log("Data type sub-options: ", dataTypeSubOptions);
      log("Source system: ", sourceSystem);
      log("Data origin: ", dataOrigin);
      log("Export reason: ", exportReason);
      return result.migrationSetId; // Return migrationSetId to the caller
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        setIsUnauthorized(true);
        setIsServerError(false);
        return null; // Return null on UnauthorizedError
      }
      if (error instanceof ServerError) {
        setIsServerError(true);
        setIsUnauthorized(false);
        return null; // Return null on ServerError
      }
      throw error;
    }
  };

  return {
    handleNewMigrationClick,
    isLoading,
    isServerError,
    isUnauthorized,
  };
};
